import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { Button } from '../ui/shadcn/button'
import { Card, CardContent, CardDescription, CardFooter, CardTitle } from '../ui/shadcn/card'
import { type ConsentStatus, cookieConsentGiven, setCookieConsent } from '@/lib/cookie'
import { usePostHog } from 'posthog-js/react'

// Held back while someone is signing up or onboarding: a banner over the
// signup form competes with the one thing we want them to do, and people who
// have already seen the product say yes far more often. Undecided users are
// still tracked in memory (no browser storage) until they choose.
const HOLD_PREFIXES = ['/login', '/auth', '/appsumo', '/company-setup']

function isHeldPath(pathname: string, search: string): boolean {
  if (HOLD_PREFIXES.some((p) => pathname.startsWith(p))) return true
  // The onboarding canvas is part of the flow (see Layout's fullscreen rule).
  return (
    pathname.startsWith('/moodboards/') && new URLSearchParams(search).get('onboarding') === '1'
  )
}

export function CookieBanner() {
  const posthog = usePostHog()
  const location = useLocation()

  const [consentGiven, setConsentGiven] = useState<ConsentStatus>(() => cookieConsentGiven())

  useEffect(() => {
    if (consentGiven !== 'undecided') {
      posthog.set_config({ persistence: consentGiven === 'yes' ? 'localStorage+cookie' : 'memory' })
    }
  }, [posthog, consentGiven])

  const visible = consentGiven === 'undecided' && !isHeldPath(location.pathname, location.search)

  // Shown vs decided gives the acceptance rate, which says whether the
  // in-memory fallback is costing us enough data to matter.
  const shownRef = useRef(false)
  useEffect(() => {
    if (!visible || shownRef.current) return
    shownRef.current = true
    posthog.capture('cookie_banner_shown', { path: location.pathname })
  }, [visible, location.pathname, posthog])

  const decide = (choice: 'yes' | 'no') => {
    posthog.capture('cookie_consent_decided', { choice, path: location.pathname })
    setCookieConsent(choice)
    setConsentGiven(choice)
  }

  if (!visible) return null

  return (
    <Card className="fixed bottom-4 left-4 right-4 z-50 p-5 shadow-lg sm:right-auto sm:w-full sm:max-w-sm">
      <CardContent>
        <CardTitle className="text-base">Help us make CASSI better</CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>
            We use first-party analytics to see which features help and where people get stuck. No
            ads, no selling data, nothing shared with third parties.
          </span>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-sm hover:underline">
            Privacy policy
          </a>
        </CardDescription>
      </CardContent>

      {/* Equal weight on purpose: a highlighted "accept" next to a muted
          "decline" is exactly what German regulators flag as nudging. */}
      <CardFooter className="gap-2">
        <Button variant="outline" className="flex-1" onClick={() => decide('yes')}>
          Accept
        </Button>
        <Button variant="outline" className="flex-1" onClick={() => decide('no')}>
          Decline
        </Button>
      </CardFooter>
    </Card>
  )
}
